import { axiosInstance } from '../common/api';

/**
 * Create a new leave request
 */
export const createLeaveRequest = (data) => {
  const url = `/leave/`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .post(url, data)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Get all leave requests for current user
 */
export const getMyLeaves = (filters = {}, pagination = {}) => {
  const params = new URLSearchParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);

  // Add pagination parameters
  if (pagination.page) params.append('page', pagination.page);
  if (pagination.pageSize) params.append('pageSize', pagination.pageSize);

  const queryString = params.toString();
  const url = `/leave/my-leaves${queryString ? `?${queryString}` : ''}`;

  return new Promise((resolve, reject) => {
    axiosInstance
      .get(url)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Get all companies (Admin only)
 */
export const getAllCompanies = () => {
  const url = `/leave/companies`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .get(url)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Get reviewers (managers and HR) for leave request
 */
export const getReviewers = (companyId = null) => {
  const params = new URLSearchParams();
  if (companyId) params.append('companyId', companyId);
  const queryString = params.toString();
  const url = `/leave/reviewers${queryString ? `?${queryString}` : ''}`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .get(url)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Get all leave requests (Admin/Manager)
 */
export const getAllLeaves = (filters = {}, pagination = {}) => {
  const params = new URLSearchParams();

  if (filters.status) params.append('status', filters.status);
  if (filters.employeeName) params.append('employeeName', filters.employeeName);
  if (filters.startDate) params.append('startDate', filters.startDate);
  if (filters.endDate) params.append('endDate', filters.endDate);
  if (filters.companyId) params.append('companyId', filters.companyId);

  // Add pagination parameters
  if (pagination.page) params.append('page', pagination.page);
  if (pagination.pageSize) params.append('pageSize', pagination.pageSize);

  const queryString = params.toString();
  const url = `/leave/${queryString ? `?${queryString}` : ''}`;

  return new Promise((resolve, reject) => {
    axiosInstance
      .get(url)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Get a single leave request by ID
 */
export const getLeaveById = (id) => {
  const url = `/leave/${id}`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .get(url)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Update leave request (Employee - own pending leaves only)
 */
export const updateLeaveRequest = (id, data) => {
  const url = `/leave/${id}`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .put(url, data)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Update leave status (Approve/Reject) - Admin/Manager only
 */
export const updateLeaveStatus = (id, data) => {
  const url = `/leave/${id}/status`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .put(url, data)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Cancel an approved leave request (HR/Admin/Manager only)
 */
export const cancelApprovedLeave = (id, data = {}) => {
  const url = `/leave/${id}/cancel-approved`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .put(url, data)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Cancel leave request (Employee - own leaves only)
 */
export const cancelLeave = (id) => {
  const url = `/leave/${id}/cancel`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .put(url)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Get remaining leaves for current user
 */
export const getRemainingLeaves = () =>
  new Promise((resolve, reject) => {
    axiosInstance
      .get('/leave/remaining')
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });

/**
 * Bulk import historical leave records for the current year (HR/Admin only, BOSC company).
 * Sends the Excel file to the backend, receives a result Excel report, and auto-downloads it.
 */
export const bulkImportLeaves = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  let response;
  try {
    response = await axiosInstance.post('/leave/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      responseType: 'blob',
    });
  } catch (err) {
    // When responseType is 'blob', error responses are also blobs — decode them
    // so callers receive a proper error message from the server.
    if (err.response?.data instanceof Blob) {
      try {
        const text = await err.response.data.text();
        const json = JSON.parse(text);
        err.response.data = json;
      } catch {
        // blob was not JSON — leave err as-is
      }
    }
    throw err;
  }

  const blob = new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;

  const contentDisposition = response.headers['content-disposition'];
  let filename = `leave_import_result_${Date.now()}.xlsx`;
  if (contentDisposition) {
    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
    if (match) filename = match[1];
  }

  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);

  return {
    total: parseInt(response.headers['x-import-total'] || '0', 10),
    success: parseInt(response.headers['x-import-success'] || '0', 10),
    failed: parseInt(response.headers['x-import-failed'] || '0', 10),
  };
};

/**
 * Get current user's leave balance for the current year
 */
export const getMyLeaveBalance = () =>
  new Promise((resolve, reject) => {
    axiosInstance
      .get('/leave-balance/me')
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });

/**
 * Upload Excel file to set leave balances (HR/Admin only)
 */
export const uploadLeaveBalances = (file, year) => {
  const formData = new FormData();
  formData.append('file', file);
  const params = year ? `?year=${year}` : '';
  return new Promise((resolve, reject) => {
    axiosInstance
      .post(`/leave-balance/upload${params}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Get all employees' leave balances (HR/Admin only)
 */
export const getAllLeaveBalances = (year, { search = '', page = 1, pageSize = 10 } = {}) => {
  const params = new URLSearchParams();
  if (year) params.append('year', year);
  if (search) params.append('search', search);
  params.append('page', page);
  params.append('pageSize', pageSize);
  return new Promise((resolve, reject) => {
    axiosInstance
      .get(`/leave-balance?${params.toString()}`)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Get aggregated intern leave balances (HR/Admin only).
 * One row per active intern with monthly-accrual totals for the given year.
 */
export const getInternBalances = (year, { search = '', page = 1, pageSize = 10, history = false } = {}) => {
  const params = new URLSearchParams();
  if (year) params.append('year', year);
  if (search) params.append('employeeName', search);
  if (history) params.append('history', 'true');
  params.append('page', page);
  params.append('pageSize', pageSize);
  return new Promise((resolve, reject) => {
    axiosInstance
      .get(`/leave/intern-balances?${params.toString()}`)
      .then((response) => resolve(response))
      .catch((err) => reject(err));
  });
};

/**
 * Export leaves to Excel
 * @param {number} month - Month number (1-12)
 * @param {number} year - Year (e.g., 2024)
 * @param {Object} filters - Filters object (status, companyId, employeeName, startDate, endDate)
 * @returns {Promise} - Excel file blob
 */
export const exportLeaves = (month, year, filters = {}) => {
  const params = new URLSearchParams();
  params.append("month", month);
  params.append("year", year);
  
  // Add all filter parameters
  if (filters.status) params.append("status", filters.status);
  if (filters.companyId) params.append("companyId", filters.companyId);
  if (filters.employeeName) params.append("employeeName", filters.employeeName);
  if (filters.startDate) params.append("startDate", filters.startDate);
  if (filters.endDate) params.append("endDate", filters.endDate);

  const url = `/leave/export?${params.toString()}`;
  return new Promise((resolve, reject) => {
    axiosInstance
      .get(url, {
        responseType: "blob", // Important for file downloads
      })
      .then((response) => {
        // Create a blob URL and trigger download
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        
        // Extract filename from Content-Disposition header or use default
        const contentDisposition = response.headers["content-disposition"];
        let filename = `Leaves_Export_${month}_${year}.xlsx`;
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        resolve(response);
      })
      .catch((err) => reject(err));
  });
};
